import {
  FileText, Image, FileSpreadsheet, File, FileCode,
  FileArchive, FileVideo, FileAudio, Presentation
} from 'lucide-react';

// File category colors (match backend defaults)
export const FILE_CATEGORIES = {
  'Agreements / Contracts': { color: '#3b82f6', icon: FileText },
  'Change Orders': { color: '#8b5cf6', icon: FileText },
  'Permits': { color: '#f97316', icon: FileText },
  'Warranties': { color: '#10b981', icon: FileText },
  'Receipts (Crew)': { color: '#ef4444', icon: FileText },
  'Receipts (Vendor)': { color: '#ec4899', icon: FileText },
  'Photos (Job Docs)': { color: '#06b6d4', icon: Image },
  'Measurement Docs': { color: '#eab308', icon: FileText },
  'Insurance Docs': { color: '#6366f1', icon: FileText },
  'Misc': { color: '#6b7280', icon: File },
};

// Get icon based on mime type
export const getFileIcon = (mimeType) => {
  if (!mimeType) return File;
  
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType === 'application/pdf') return FileText;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return Presentation;
  if (mimeType.includes('document') || mimeType.includes('word')) return FileText;
  if (mimeType.startsWith('video/')) return FileVideo;
  if (mimeType.startsWith('audio/')) return FileAudio;
  if (mimeType.includes('zip') || mimeType.includes('rar')) return FileArchive;
  if (mimeType.startsWith('text/')) return FileCode;
  
  return File;
};

// Format file size
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Check if file is previewable
export const isPreviewable = (mimeType) => {
  const previewable = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'application/pdf',
    'text/plain', 'text/csv', 'text/html'
  ];
  return previewable.includes(mimeType);
};

// Visibility options
export const VISIBILITY_OPTIONS = [
  { value: 'internal', label: 'Internal Only', description: 'Only visible to team members' },
  { value: 'customer', label: 'Customer Visible', description: 'Can be shared with customers' }
];

// Quick filter chips
export const QUICK_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'signed-agreements', label: 'Signed Agreements', category: 'Agreements / Contracts', isSignedEstimate: true },
  { id: 'agreements', label: 'Agreements', category: 'Agreements / Contracts' },
  { id: 'receipts', label: 'Receipts', categories: ['Receipts (Crew)', 'Receipts (Vendor)'] },
  { id: 'photos', label: 'Photos', category: 'Photos (Job Docs)' },
  { id: 'warranties', label: 'Warranties', category: 'Warranties' },
  { id: 'permits', label: 'Permits', category: 'Permits' },
  { id: 'internal', label: 'Internal', visibility: 'internal' },
  { id: 'customer', label: 'Customer-visible', visibility: 'customer' }
];

// Audit action labels
export const AUDIT_ACTIONS = {
  uploaded: { label: 'Uploaded', color: 'text-green-600' },
  downloaded: { label: 'Downloaded', color: 'text-blue-600' },
  renamed: { label: 'Renamed', color: 'text-amber-600' },
  moved: { label: 'Moved', color: 'text-purple-600' },
  tagged: { label: 'Tagged', color: 'text-cyan-600' },
  visibility_changed: { label: 'Visibility changed', color: 'text-orange-600' },
  deleted: { label: 'Deleted', color: 'text-red-600' },
  restored: { label: 'Restored', color: 'text-emerald-600' },
  version_added: { label: 'New version added', color: 'text-indigo-600' },
  linked: { label: 'Linked to entity', color: 'text-sky-600' },
  unlinked: { label: 'Unlinked from entity', color: 'text-slate-600' },
  category_changed: { label: 'Category changed', color: 'text-violet-600' }
};
